import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	Alert,
	App,
	Button,
	Empty,
	Input,
	Modal,
	Popconfirm,
	Radio,
	Select,
	Skeleton,
	Switch,
	Table,
	Tag,
	Typography,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';

import { copyTextToClipboard } from '../../utils/clipboard';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { nestSubCollections, nestedOptionClass } from '../../utils/collectionFamily';
import { describeLastClient } from '../../utils/agentClients';
import useDateFormat from '../../hooks/useDateFormat';
import { PLUS } from '../icons';
import ToggleRow from '../common/ToggleRow';
import {
	apiKeyScopeLabel,
	apiKeyStatus,
	createApiKey,
	deleteScopeAllowed,
	keyPredatesDeleteScope,
	listApiKeys,
	revokeApiKey,
	CARDS_SCOPE_LABEL,
	CARDS_SCOPE_NOTE,
	DELETE_SCOPE_LABEL,
	DELETE_SCOPE_NEEDS_WRITE,
	DELETE_SCOPE_NOTE,
	EXPIRY_OPTIONS,
	MAX_ACTIVE_KEYS,
	ONE_TIME_TOKEN_NOTE,
	PRE_DELETE_SCOPE_NOTICE,
	SCOPE_OPTIONS,
} from '../../services/apiKeys';
import { destructivePopconfirmProps } from '../common/confirmDestructive';
import { useCollections } from '../../contexts/CollectionsContext';

const { Text } = Typography;

const STATUS_TAGS = {
	active: { color: 'green', label: 'Active' },
	expired: { color: 'default', label: 'Expired' },
	revoked: { color: 'red', label: 'Revoked' },
};

// Blank form. `collectionMode` is the picker's own state — the API wants
// the list omitted for "all", never an empty array. `cards` is the third
// scope, off until asked for: it is what lets a key redesign the data.
// `canDelete` is the fourth, off until asked for too, and only live beside
// write or cards: it is what lets a key destroy what it could change.
const EMPTY_FORM = { name: '', scope: 'read', cards: false, canDelete: false, collectionMode: 'all', collectionIds: [], expiresIn: null };

// Names shown inline under "N collections" before it turns into "+k more".
const MAX_NAMES_INLINE = 3;

/**
 * "API keys" on the account page: the keys this user has minted for machine
 * clients (a script, a Claude agent, another tool), each a scoped slice of
 * the user's own authority — read or read+write, every collection or a
 * chosen few, optionally expiring. Never admin, whatever the user is.
 *
 * The token is shown ONCE, in the create response; the listing knows only
 * the prefix. A successful create therefore swaps to a reveal modal rather
 * than adding a row that could ever show the secret again. Revoking is
 * immediate and cannot be undone — hence the confirm.
 */
export default function ApiKeysPanel() {
	const { message } = App.useApp();
	useDateFormat();

	const [keys, setKeys] = useState(null);
	const [loadError, setLoadError] = useState(null);
	const [showRevoked, setShowRevoked] = useState(false);
	// The caller's collections, for the picker and for resolving names.
	const { collections } = useCollections();

	const [formOpen, setFormOpen] = useState(false);
	const [form, setForm] = useState(EMPTY_FORM);
	const [creating, setCreating] = useState(false);
	// The one-time reveal: { key, token }.
	const [created, setCreated] = useState(null);

	const load = useCallback(() => {
		let active = true;
		listApiKeys({ includeRevoked: showRevoked })
			.then(rows => { if (active) { setKeys(rows); setLoadError(null); } })
			.catch(error => { if (active) setLoadError(error); });
		return () => { active = false; };
	}, [showRevoked]);

	useEffect(load, [load]);


	const collectionNameById = useMemo(() => {
		const map = {};
		collections.forEach(collection => {
			const id = normalizeId(collection?._id);
			if (id) map[id] = collection?.name || id;
		});
		return map;
	}, [collections]);

	// Alphabetical, each sub-collection indented beneath its parent.
	const collectionOptions = useMemo(() => nestSubCollections(collections
		.filter(collection => normalizeId(collection?._id))
		.sort((a, b) => (a?.name || '').localeCompare(b?.name || '')))
		.map(({ collection, depth }) => ({
			value: normalizeId(collection._id),
			label: collection.name || '',
			className: nestedOptionClass(depth),
		})), [collections]);

	const activeCount = useMemo(
		() => (keys || []).filter(key => apiKeyStatus(key) === 'active').length,
		[keys]
	);

	// Keys minted before the delete scope existed lost deletion on purpose —
	// nothing was grandfathered — so a person whose tool suddenly gets a
	// refusal is told why here, once, above the list.
	const anyPredatesDelete = useMemo(
		() => (keys || []).some(key => apiKeyStatus(key) === 'active' && keyPredatesDeleteScope(key)),
		[keys]
	);

	const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

	const closeForm = useCallback(() => {
		setFormOpen(false);
		setForm(EMPTY_FORM);
	}, []);

	const nameValid = form.name.trim().length > 0 && form.name.trim().length <= 80;
	const collectionsValid = form.collectionMode === 'all' || form.collectionIds.length > 0;
	const canSubmit = nameValid && collectionsValid && !creating;
	// Read only and no cards: there is nothing to delete with, so the switch
	// reads off and cannot be turned on (the API refuses delete alone).
	const deleteAllowed = deleteScopeAllowed(form.scope, form.cards);

	const handleCreate = useCallback(async () => {
		if (!canSubmit) return;
		setCreating(true);
		try {
			const result = await createApiKey({
				name: form.name,
				scopes: form.scope,
				cards: form.cards,
				canDelete: form.canDelete && deleteAllowed,
				collectionIds: form.collectionMode === 'some' ? form.collectionIds : undefined,
				expiresIn: form.expiresIn || undefined,
			});
			closeForm();
			setCreated(result);
			load();
		} catch (error) {
			message.error(error?.response?.data?.message || 'Could not create the API key.');
		} finally {
			setCreating(false);
		}
	}, [canSubmit, form, deleteAllowed, closeForm, load, message]);

	const handleRevoke = useCallback(async (id) => {
		try {
			await revokeApiKey(id);
			message.success('API key revoked.');
		} catch (error) {
			message.error(error?.response?.data?.message || 'Could not revoke the API key.');
		}
		load();
	}, [load, message]);

	const handleCopy = useCallback(async () => {
		if (await copyTextToClipboard(created?.token)) message.success('API key copied to clipboard');
		else message.error('Unable to copy the API key');
	}, [created, message]);

	const renderCollections = (collectionIds) => {
		if (!Array.isArray(collectionIds) || collectionIds.length === 0) return 'All collections';
		const names = collectionIds.map(id => collectionNameById[normalizeId(id)]).filter(Boolean);
		const shown = names.slice(0, MAX_NAMES_INLINE);
		const more = names.length - shown.length;
		return (
			<>
				{collectionIds.length === 1 ? '1 collection' : `${collectionIds.length} collections`}
				{shown.length ? (
					<span className="api-keys__collections-names" title={names.join(', ')}>
						{shown.join(', ')}{more > 0 ? ` +${more} more` : ''}
					</span>
				) : null}
			</>
		);
	};

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			render: (name, row) => {
				// Which keepr client last used the key, and whether it is behind.
				// Absent on keys only ever used directly, and on an older API.
				const client = describeLastClient(row.lastClient);
				return (
					<>
						<span className="api-keys__name">{name}</span>
						<span className="api-keys__prefix">{row.prefix}…</span>
						{client ? (
							<span className="api-keys__client" data-testid="api-key-client">
								<span>{client.label}</span>
								{client.outOfDate ? (
									<>
										<Tag color="warning" className="api-keys__client-tag">Out of date</Tag>
										<Link to={client.updateHref}>How to update</Link>
									</>
								) : null}
							</span>
						) : null}
					</>
				);
			},
		},
		{
			title: 'Scope',
			key: 'scopes',
			width: 120,
			render: (_, row) => <Tag>{apiKeyScopeLabel(row)}</Tag>,
		},
		{
			title: 'Collections',
			dataIndex: 'collectionIds',
			key: 'collections',
			render: renderCollections,
		},
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 130,
			render: value => <span className="api-keys__meta">{value ? formatDisplayDate(value) : '—'}</span>,
		},
		{
			title: 'Last used',
			dataIndex: 'lastUsedAt',
			key: 'lastUsedAt',
			width: 160,
			render: value => <span className="api-keys__meta">{value ? formatDisplayDateTime(value) : 'never'}</span>,
		},
		{
			title: 'Expires',
			dataIndex: 'expiresAt',
			key: 'expiresAt',
			width: 130,
			render: value => <span className="api-keys__meta">{value ? formatDisplayDate(value) : 'Never'}</span>,
		},
		{
			title: 'Status',
			key: 'status',
			width: 100,
			render: (_, row) => {
				const status = STATUS_TAGS[apiKeyStatus(row)];
				return <Tag color={status.color}>{status.label}</Tag>;
			},
		},
		{
			key: 'actions',
			width: 90,
			align: 'right',
			render: (_, row) => (apiKeyStatus(row) === 'revoked' ? null : (
				<Popconfirm
					title="Revoke this key?"
					description="Anything using it stops working immediately. This cannot be undone."
					{...destructivePopconfirmProps('Revoke')}
					onConfirm={() => handleRevoke(row.id)}
				>
					<Button type="text" danger size="small" aria-label={`Revoke API key ${row.name}`}>
						Revoke
					</Button>
				</Popconfirm>
			)),
		},
	];

	const emptyState = (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description={(
				<div className="api-keys__empty">
					<strong>No API keys yet.</strong>
					<p className="mb-0 mt-2">
						An API key lets a machine client — a script, a Claude agent, another tool — use
						keepr on your behalf.
					</p>
					<ul>
						<li>A key acts as you: it can do at most what you can, only in the collections you allow.</li>
						<li>Keys are never admin, whatever your account is.</li>
						<li>Revoke a key at any time; it stops working immediately.</li>
					</ul>
				</div>
			)}
		/>
	);

	let body;
	if (loadError) {
		body = (
			<Alert
				type="error"
				showIcon
				message="Could not load your API keys."
				action={<Button size="small" onClick={load}>Retry</Button>}
			/>
		);
	} else if (keys === null) {
		body = <Skeleton active paragraph={{ rows: 3 }} />;
	} else if (keys.length === 0) {
		body = emptyState;
	} else {
		body = (
			<div className="api-keys__table">
				<Table
					size="small"
					rowKey="id"
					columns={columns}
					dataSource={keys}
					pagination={false}
					scroll={{ x: true }}
				/>
			</div>
		);
	}

	const revealTitle = created?.key?.name ? `API key "${created.key.name}" created` : 'API key created';

	return (
		<section className="api-keys">
			<h3>API keys</h3>
			<Text type="secondary" className="api-keys__intro">
				Credentials for scripts, agents and other tools. A key acts as you — read or
				read-and-write, in every collection or just the ones you pick — and you can
				revoke it at any time.
			</Text>

			<div className="api-keys__toolbar">
				<Button
					icon={<PLUS />}
					type="primary"
					onClick={() => setFormOpen(true)}
					disabled={activeCount >= MAX_ACTIVE_KEYS}
				>
					Create key
				</Button>
				<label className="api-keys__toggle">
					<Switch size="small" checked={showRevoked} onChange={setShowRevoked} />
					Show revoked
				</label>
			</div>

			{anyPredatesDelete ? (
				<Alert type="info" showIcon className="api-keys__notice" message={PRE_DELETE_SCOPE_NOTICE} />
			) : null}

			{body}

			<Modal
				open={formOpen}
				title="Create API key"
				okText="Create key"
				okButtonProps={{ disabled: !canSubmit }}
				confirmLoading={creating}
				onOk={handleCreate}
				onCancel={closeForm}
				destroyOnClose
				className="api-keys__modal"
			>
				<div className="api-keys__field">
					<label className="api-keys__field-label" htmlFor="api-key-name">Name</label>
					<Input
						id="api-key-name"
						maxLength={80}
						value={form.name}
						placeholder="e.g. Claude OAR import"
						onChange={event => setField('name', event.target.value)}
						autoFocus
					/>
					<p className="api-keys__field-hint">What this key is for — you'll see it in the list.</p>
				</div>

				<div className="api-keys__field">
					<span className="api-keys__field-label" id="api-key-scope-label">Scope</span>
					<Radio.Group
						aria-labelledby="api-key-scope-label"
						value={form.scope}
						onChange={event => setField('scope', event.target.value)}
					>
						{SCOPE_OPTIONS.map(option => (
							<Radio key={option.value} value={option.value}>{option.label}</Radio>
						))}
					</Radio.Group>
					<p className="api-keys__field-hint">Write includes read. Neither can share, delete a collection, or manage keys.</p>
					<ToggleRow
						id="api-key-cards"
						first
						label={CARDS_SCOPE_LABEL}
						description={CARDS_SCOPE_NOTE}
						checked={form.cards}
						onChange={value => setField('cards', value)}
					/>
					<ToggleRow
						id="api-key-delete"
						label={DELETE_SCOPE_LABEL}
						description={deleteAllowed ? DELETE_SCOPE_NOTE : DELETE_SCOPE_NEEDS_WRITE}
						checked={form.canDelete && deleteAllowed}
						disabled={!deleteAllowed}
						onChange={value => setField('canDelete', value)}
					/>
				</div>

				<div className="api-keys__field">
					<span className="api-keys__field-label" id="api-key-collections-label">Collections</span>
					<Radio.Group
						aria-labelledby="api-key-collections-label"
						value={form.collectionMode}
						onChange={event => setField('collectionMode', event.target.value)}
					>
						<Radio value="all">All collections I can access</Radio>
						<Radio value="some">Only these collections</Radio>
					</Radio.Group>
					{form.collectionMode === 'some' ? (
						<Select
							mode="multiple"
							id="api-key-collections"
							aria-label="Collections"
							className="api-keys__select mt-2"
							placeholder="Pick collections"
							value={form.collectionIds}
							options={collectionOptions}
							optionFilterProp="label"
							onChange={value => setField('collectionIds', value)}
						/>
					) : null}
				</div>

				<div className="api-keys__field">
					<label className="api-keys__field-label" htmlFor="api-key-expiry">Expires</label>
					<Select
						id="api-key-expiry"
						className="api-keys__select"
						value={form.expiresIn}
						options={EXPIRY_OPTIONS}
						onChange={value => setField('expiresIn', value)}
					/>
				</div>
			</Modal>

			<Modal
				open={!!created}
				title={revealTitle}
				onCancel={() => setCreated(null)}
				footer={<Button type="primary" onClick={() => setCreated(null)}>Done</Button>}
				destroyOnClose
				className="api-keys__reveal"
			>
				<Alert type="warning" showIcon message={ONE_TIME_TOKEN_NOTE} />
				<code className="api-keys__token" data-testid="api-key-token">{created?.token}</code>
				<Button icon={<CopyOutlined />} onClick={handleCopy}>Copy key</Button>
				<p className="api-keys__field-hint mt-3">
					Send it as <code>Authorization: Bearer {created?.key?.prefix}…</code> on any request.
				</p>
			</Modal>
		</section>
	);
}
